import { api } from '@/lib/apiClient'
import type { Envelope } from '@/lib/apiTypes'

/** Free-form criteria object — keys gated server-side by tier (none/type/all). */
export type SearchCriteria = Record<string, unknown>

export interface Search {
  id: string
  name: string | null
  communeCode: string | null
  latitude: number | null
  longitude: number | null
  radiusKm: number
  propertyTypes: string[]
  criteria: SearchCriteria
  /** Digest e-mail du lundi. Sans effet d'envoi hors tiers payants (veille, prospection). */
  weeklyEnabled: boolean
  /** Worklist e-mail quotidienne (jours ouvrés). Disponible pour tous les tiers. */
  dailyEnabled: boolean
  lastZoneChangedAt: string | null
  zoneChangeCount: number
  createdAt: string
  /** Zone active : false ⇒ désactivée (lisible mais ne génère plus d'opportunités). */
  active: boolean
  /** Date d'extinction (ISO) si désactivée, sinon null. */
  deactivatedAt: string | null
}

export interface SearchQuota {
  searchesUsed: number
  searchesMax: number
}

/**
 * Forme enrichie renvoyée UNIQUEMENT par GET /account/searches : la base `Search`
 * plus `sentCount`, le cumul de biens visibles (avec estimation) reçus sous la zone.
 * Les endpoints show/create/update renvoient `Search` sans ce champ.
 */
export interface SearchWithCount extends Search {
  /** Nombre de biens emailés sous cette zone (≥ 0). */
  sentCount: number
  /** Biens emailés non encore vus in-app (notifiés moins vus) — pilote le badge nav. */
  unseenCount: number
}

/** Centre is EXACTLY one form: a commune code, or a free lat/lng point. */
export type SearchCenter =
  | { communeCode: string; lat?: undefined; lng?: undefined }
  | { lat: number; lng: number; communeCode?: undefined }

export type SearchInput = SearchCenter & {
  radiusKm?: number
  name?: string
  criteria?: SearchCriteria
}

/**
 * PATCH payload: criteria/name/digest free; radiusKm/lat+lng = zone change (cooldown).
 * `weeklyEnabled`/`dailyEnabled` are digest prefs and never trigger the zone delay.
 */
export type SearchPatch = {
  name?: string
  criteria?: SearchCriteria
  radiusKm?: number
  lat?: number
  lng?: number
  weeklyEnabled?: boolean
  dailyEnabled?: boolean
}

/**
 * Valeurs d'enum renommées au catalogue que le back accepte encore sur les zones
 * enregistrées. Relues sous leur nom courant : sinon le sélecteur les compte sans
 * les cocher. Le prochain enregistrement de la zone envoie donc le nouveau nom.
 */
const RENAMED_VALUES: Record<string, Record<string, string>> = {
  typologie_logement: { T7plus: 'T7 ou plus' },
}

function migrateCriteria(criteria: SearchCriteria | null | undefined): SearchCriteria {
  if (!criteria) return {}
  const out = { ...criteria }
  for (const [key, renamed] of Object.entries(RENAMED_VALUES)) {
    const v = out[key]
    if (Array.isArray(v)) out[key] = v.map((x) => (typeof x === 'string' ? (renamed[x] ?? x) : x))
    else if (typeof v === 'string') out[key] = renamed[v] ?? v
  }
  return out
}

/**
 * Le back (Rails) sérialise les `id` en nombres, alors que le contrat les déclare
 * `string`. On normalise à la frontière pour que le type `Search.id: string` soit
 * vrai au runtime : sinon toute comparaison contre un id issu de l'URL (toujours
 * une string) échoue silencieusement (`20 !== '20'`). Les critères y sont aussi
 * migrés (`RENAMED_VALUES`).
 */
function normalizeSearch<T extends Search>(s: T): T {
  return { ...s, id: String(s.id), criteria: migrateCriteria(s.criteria) }
}

export async function listSearches(): Promise<{ data: SearchWithCount[]; quota: SearchQuota }> {
  const res = await api.get<Envelope<SearchWithCount[]> & { quota: SearchQuota }>('/account/searches')
  return { data: res.data.data.map(normalizeSearch), quota: res.data.quota }
}

export async function getSearch(id: string): Promise<Search> {
  const res = await api.get<Envelope<Search>>(`/account/searches/${id}`)
  return normalizeSearch(res.data.data)
}

export async function createSearch(body: SearchInput): Promise<Search> {
  const res = await api.post<Envelope<Search>>('/account/searches', body)
  return normalizeSearch(res.data.data)
}

export async function updateSearch(id: string, body: SearchPatch): Promise<Search> {
  const res = await api.patch<Envelope<Search>>(`/account/searches/${id}`, body)
  return normalizeSearch(res.data.data)
}

/**
 * Désactivation douce (le bouton « supprimer » de l'UI) : la zone n'est PAS détruite,
 * elle reste lisible mais ne génère plus d'opportunités et libère son slot de quota.
 * `purgeListings` supprime en plus les biens déjà reçus sous cette zone.
 */
export async function deactivateSearch(id: string, purgeListings = false): Promise<Search> {
  const res = await api.delete<Envelope<Search>>(`/account/searches/${id}`, {
    params: { purge_listings: purgeListings },
  })
  return normalizeSearch(res.data.data)
}

/** Réactive une zone désactivée. Lève une 409 si le quota de zones actives est atteint. */
export async function reactivateSearch(id: string): Promise<Search> {
  const res = await api.post<Envelope<Search>>(`/account/searches/${id}/reactivate`)
  return normalizeSearch(res.data.data)
}

/** Suppression définitive (RGPD) : détruit la zone et son historique de biens. */
export async function permanentDeleteSearch(id: string): Promise<void> {
  await api.delete(`/account/searches/${id}/permanent`)
}

// ── Filter catalogue ─────────────────────────────────────────────────────────

export type { FilterCategory, FilterDef } from '@/lib/filterCatalog'
import { relabelOptions, type FilterCategory } from '@/lib/filterCatalog'

export async function getFilterCatalog(): Promise<{
  categories: FilterCategory[]
  filtersCount: number
}> {
  const res = await api.get<
    Envelope<{ categories: FilterCategory[]; filters_count: number }>
  >('/account/searches/filters')
  relabelOptions(res.data.data.categories)
  return { categories: res.data.data.categories, filtersCount: res.data.data.filters_count }
}

// ── Live preview ─────────────────────────────────────────────────────────────

export interface PreviewSample {
  identifiantDpe: string
  estimationPrix: number | null
  estimationBasse: number | null
  estimationHaute: number | null
  prixM2: number | null
  fiabilite: string | null
  classeEnergie: string | null
  classeGes: string | null
  surface: number | null
  typeBatiment: string | null
  anneeConstruction: number | null
  address: string | null
  commune: string | null
  codePostal: string | null
  distanceM: number | null
  dateEtablissement: string | null
  latitude: number | null
  longitude: number | null
  [k: string]: unknown
}

export interface PreviewResult {
  count: number
  /** perMonth : moyenne mensuelle de nouveaux biens matchés (matching / 12, arrondi). */
  counters: { inRadius: number; matching: number; perMonth: number }
  samples: PreviewSample[]
  byDpeClass: Record<string, number>
  bySurface: Record<string, number>
}

/** Brut renvoyé par /preview : snake_case imbriqué, aplati par mapPreviewSample. */
interface RawPreviewSample {
  identifiant_dpe: string
  classe_energie?: string | null
  classe_ges?: string | null
  surface?: number | null
  type_batiment?: string | null
  annee_construction?: number | null
  distance_m?: number | null
  date_etablissement?: string | null
  adresse?: {
    numero?: string | null
    rue?: string | null
    code_postal?: string | null
    commune?: string | null
  } | null
  estimation?: {
    prix_median?: number | null
    prix_bas?: number | null
    prix_haut?: number | null
    prix_m2?: number | null
    fiabilite?: string | null
  } | null
  coordinates?: { lat?: number | null; lon?: number | null } | null
  [k: string]: unknown
}

type RawPreviewResult = Omit<PreviewResult, 'samples'> & { samples: RawPreviewSample[] }

function mapPreviewSample(s: RawPreviewSample): PreviewSample {
  // Adresse sans le numéro : l'aperçu est un teaser, on ne révèle pas le numéro exact.
  const address = s.adresse?.rue ?? null
  return {
    identifiantDpe: s.identifiant_dpe,
    classeEnergie: s.classe_energie ?? null,
    classeGes: s.classe_ges ?? null,
    surface: s.surface ?? null,
    typeBatiment: s.type_batiment ?? null,
    anneeConstruction: s.annee_construction ?? null,
    estimationPrix: s.estimation?.prix_median ?? null,
    estimationBasse: s.estimation?.prix_bas ?? null,
    estimationHaute: s.estimation?.prix_haut ?? null,
    prixM2: s.estimation?.prix_m2 ?? null,
    fiabilite: s.estimation?.fiabilite ?? null,
    address,
    commune: s.adresse?.commune ?? null,
    codePostal: s.adresse?.code_postal ?? null,
    distanceM: s.distance_m ?? null,
    dateEtablissement: s.date_etablissement ?? null,
    latitude: s.coordinates?.lat ?? null,
    longitude: s.coordinates?.lon ?? null,
  }
}

export async function previewSearch(body: SearchInput, signal?: AbortSignal): Promise<PreviewResult> {
  const res = await api.post<Envelope<RawPreviewResult>>('/account/searches/preview', body, { signal })
  const raw = res.data.data
  return { ...raw, samples: raw.samples.map(mapPreviewSample) }
}
